/**
 * Budget roll-up (Phase 17).
 *
 * Pure functions over already-loaded rows. No Supabase, no DOM — this is money
 * math, and it needs to be testable to the cent.
 *
 * A material's budget category is RESOLVED through a chain:
 *   1. specs.budget_category_id                     — per-material override
 *   2. its material category's budget_category_id   — the default mapping
 *   3. none                                          — Uncategorized
 *
 * Uncategorized is a real bucket, not a discard. A material nobody has filed
 * is still money that will be spent, and leaving it out of the project total
 * would understate the budget — the one error a budget tool must never make.
 */

import type {
  SpecRow, BudgetCategoryRow, MaterialCategoryRow, BudgetLineRow, ProjectBudgetRow,
} from '@/types/database';
import { extendedCostCents } from '@/lib/spec-table';
import { fmtMoney } from '@/lib/dom';
import {
  summarizeChangeOrders, revisedBudgetCents, type ChangeOrderMoney, type ChangeOrderTotals,
} from '@/lib/change-order-budget';

/** Sentinel id for the bucket holding materials with no budget category. */
export const UNCATEGORIZED = '__uncategorized__';

export interface BudgetRow {
  /** budget_categories.id, or UNCATEGORIZED. */
  categoryId: string;
  name: string;
  /** Summed from the project's materials. */
  rolledMaterialsCents: number;
  /** Typed in by the designer on the budget line. */
  manualMaterialsCents: number;
  laborCents: number;
  /** rolled + manual + labor */
  totalCents: number;
  /** How many materials rolled into this row. */
  specCount: number;
  /**
   * True when the row has BOTH rolled-up materials and a manual amount.
   * Probably double-counted — surfaced, never blocked, because "rooms costed
   * AND a lump allowance" is a legitimate mid-project state.
   */
  possibleDoubleCount: boolean;
  /** The synthetic Uncategorized row rather than a real category. */
  isUncategorized: boolean;
}

export interface BudgetSummary {
  rows: BudgetRow[];
  /** Every category row added up — materials and labor, no designer fee. */
  subtotalCents: number;
  designerFeeCents: number;
  /** Design fees on APPROVED change orders — a real cost, like the base fee. */
  changeOrderFeeCents: number;
  /** subtotal + designer fee + change-order fees. What the project costs. */
  projectTotalCents: number;
  /** projects.budget_cents as typed: the ORIGINAL budget. Null when unset. */
  originalBudgetCents: number | null;
  /** Approved and pending change-order money (change-order-budget.ts). */
  changeOrders: ChangeOrderTotals;
  /**
   * The budget everything is measured against: the REVISED budget —
   * original + approved change orders. Null when no original is set.
   */
  totalBudgetCents: number | null;
  /** target − projectTotal. Negative means over budget. Null when no target. */
  varianceCents: number | null;
  /** True only when a target exists and has been exceeded. */
  isOverBudget: boolean;
  /** Materials with no resolved budget category. */
  uncategorizedSpecCount: number;
  /** Rows flagged as possibly double-counted. */
  doubleCountedCategoryIds: string[];
}

/**
 * Which budget category a material belongs to, or null.
 *
 * Exported because the materials table shows the resolved value too, and two
 * implementations of a resolution chain is how they drift apart.
 */
export function resolveBudgetCategoryId(
  spec: Pick<SpecRow, 'budget_category_id' | 'material_category_id'>,
  materialCategories: Pick<MaterialCategoryRow, 'id' | 'budget_category_id'>[],
): string | null {
  if (spec.budget_category_id) return spec.budget_category_id;
  if (!spec.material_category_id) return null;
  const mc = materialCategories.find((m) => m.id === spec.material_category_id);
  return mc?.budget_category_id ?? null;
}

/**
 * Group materials by the budget category they roll into.
 *
 * Shared by the roll-up and the drill-down deliberately: two implementations of
 * the resolution chain would drift, and the drill-down's whole job is showing
 * you the exact materials behind a number. If they disagreed, the feature would
 * be worse than not having it.
 *
 * Every spec lands somewhere — unknown or missing categories go to
 * UNCATEGORIZED rather than being dropped.
 */
export function groupSpecsByBudgetCategory(
  specs: SpecRow[],
  materialCategories: Pick<MaterialCategoryRow, 'id' | 'budget_category_id'>[],
  budgetCategories: Pick<BudgetCategoryRow, 'id'>[],
): Map<string, SpecRow[]> {
  const out = new Map<string, SpecRow[]>();
  for (const s of specs) {
    const resolved = resolveBudgetCategoryId(s, materialCategories);
    const known = resolved != null && budgetCategories.some((c) => c.id === resolved);
    const key = known ? resolved! : UNCATEGORIZED;
    const list = out.get(key);
    if (list) list.push(s); else out.set(key, [s]);
  }
  return out;
}

export interface BudgetInput {
  specs: SpecRow[];
  budgetCategories: BudgetCategoryRow[];
  materialCategories: MaterialCategoryRow[];
  budgetLines: BudgetLineRow[];
  /**
   * Designer fee only. The budget TARGET deliberately no longer comes from
   * here — see `budgetCents` below. Narrowing this Pick is what makes the
   * compiler find any caller still reading the old column.
   */
  projectBudget: Pick<ProjectBudgetRow, 'designer_fee_cents'> | null;
  /**
   * `projects.budget_cents` — the single source of truth for the budget target.
   *
   * It used to be `project_budgets.total_budget_cents`, which meant the hero
   * card and the Budget section were reading two different columns and could
   * disagree indefinitely. `projects.budget_cents` won because the client
   * portal already reads it: `get_shared_project` embeds it, as do five
   * superseded migrations. Moving the other way would have meant restating a
   * SECURITY DEFINER portal RPC for a cosmetic gain.
   *
   * Migration 0025 backfilled it from the old column where it was unset.
   */
  budgetCents: number | null;
  /**
   * The project's change orders (0038). Approved ones raise the budget and
   * add their design fee to projected cost; sent ones are reported as
   * pending. Optional so callers without them get the old behavior exactly.
   */
  changeOrders?: readonly ChangeOrderMoney[];
}

export function buildBudget(input: BudgetInput): BudgetSummary {
  const { specs, budgetCategories, materialCategories, budgetLines, projectBudget, budgetCents } = input;

  const grouped = groupSpecsByBudgetCategory(specs, materialCategories, budgetCategories);
  const rolled = new Map<string, { cents: number; count: number }>();
  for (const [key, list] of grouped) {
    rolled.set(key, {
      // A material with no price still counts toward the item count — "3 items,
      // $0 so far" is more honest than pretending the row is empty.
      cents: list.reduce((t, sp) => t + (extendedCostCents(sp) ?? 0), 0),
      count: list.length,
    });
  }

  const lineFor = (categoryId: string): BudgetLineRow | undefined =>
    budgetLines.find((l) => l.budget_category_id === categoryId);

  const rows: BudgetRow[] = budgetCategories.map((c) => {
    const r = rolled.get(c.id) ?? { cents: 0, count: 0 };
    const line = lineFor(c.id);
    const manual = line?.materials_manual_cents ?? 0;
    const labor = line?.labor_cents ?? 0;
    return {
      categoryId: c.id,
      name: c.name,
      rolledMaterialsCents: r.cents,
      manualMaterialsCents: manual,
      laborCents: labor,
      totalCents: r.cents + manual + labor,
      specCount: r.count,
      possibleDoubleCount: r.cents > 0 && manual > 0,
      isUncategorized: false,
    };
  });

  // The Uncategorized row appears only when it has something in it, so a
  // fully-filed project doesn't carry a permanent empty row.
  const un = rolled.get(UNCATEGORIZED);
  if (un && un.count > 0) {
    rows.push({
      categoryId: UNCATEGORIZED,
      name: 'Uncategorized',
      rolledMaterialsCents: un.cents,
      manualMaterialsCents: 0,
      laborCents: 0,
      totalCents: un.cents,
      specCount: un.count,
      possibleDoubleCount: false,
      isUncategorized: true,
    });
  }

  const subtotalCents = rows.reduce((t, r) => t + r.totalCents, 0);
  const designerFeeCents = projectBudget?.designer_fee_cents ?? 0;
  const changeOrders = summarizeChangeOrders(input.changeOrders ?? []);
  const changeOrderFeeCents = changeOrders.approvedFeeCents;
  const projectTotalCents = subtotalCents + designerFeeCents + changeOrderFeeCents;
  const originalBudgetCents = budgetCents ?? null;
  const totalBudgetCents = revisedBudgetCents(originalBudgetCents, changeOrders);

  return {
    rows,
    subtotalCents,
    designerFeeCents,
    changeOrderFeeCents,
    projectTotalCents,
    originalBudgetCents,
    changeOrders,
    totalBudgetCents,
    varianceCents: totalBudgetCents == null ? null : totalBudgetCents - projectTotalCents,
    // No target is not "under budget" — it's unknown. Don't render it green.
    isOverBudget: totalBudgetCents != null && projectTotalCents > totalBudgetCents,
    uncategorizedSpecCount: un?.count ?? 0,
    doubleCountedCategoryIds: rows.filter((r) => r.possibleDoubleCount).map((r) => r.categoryId),
  };
}

/**
 * Label for the labor column.
 *
 * Clients see "Services or subcontractor fees" — "Labor" reads as a line item
 * they're being charged for rather than a scope of work.
 */
/**
 * Labor / Materials / Fees, for the dashboard summary.
 *
 * `BudgetSummary.subtotalCents` lumps labor and materials together because the
 * budget TABLE shows them per category. The dashboard wants them apart, so
 * this re-splits across every row.
 *
 * Materials is rolled + manual combined. Those are two ways of recording the
 * same spend — priced materials rolled up from rooms, and a lump allowance the
 * designer typed — and a glance surface should not make that distinction. The
 * budget table still separates them, and still flags rows carrying both as a
 * possible double count.
 *
 * Invariant, asserted in tests: labor + materials + fees === projectTotalCents.
 * If that ever fails, the dashboard and the budget table are disagreeing about
 * the same project, which is the exact bug this whole change exists to end.
 */
export interface BudgetBreakdown {
  laborCents: number;
  materialsCents: number;
  feesCents: number;
  /** Equals BudgetSummary.projectTotalCents. */
  totalCents: number;
}

/**
 * How to tone the variance figure.
 *
 * Extracted because the hero summary and the budget table both need it, and a
 * second copy of "null is muted, over is red, otherwise green" is a second
 * place for the two surfaces to disagree — which is the whole failure this
 * change set out to end.
 *
 * No target is NOT "under budget". An unknown target rendered green claims the
 * project is on track when nobody has said what on-track means.
 */
export type VarianceTone = 'muted' | 'budget-over' | 'budget-under';

export function varianceTone(summary: BudgetSummary): VarianceTone {
  if (summary.varianceCents == null) return 'muted';
  return summary.isOverBudget ? 'budget-over' : 'budget-under';
}

/** True when the variance figure should read as prose rather than money. */
export function hasNoTarget(summary: BudgetSummary): boolean {
  return summary.varianceCents == null;
}

export function budgetBreakdown(summary: BudgetSummary): BudgetBreakdown {
  let laborCents = 0;
  let materialsCents = 0;
  for (const r of summary.rows) {
    laborCents += r.laborCents;
    materialsCents += r.rolledMaterialsCents + r.manualMaterialsCents;
  }
  return {
    laborCents,
    materialsCents,
    feesCents: summary.designerFeeCents + summary.changeOrderFeeCents,
    totalCents: summary.projectTotalCents,
  };
}

export function laborLabel(audience: 'designer' | 'client'): string {
  return audience === 'client' ? 'Services or subcontractor fees' : 'Labor';
}

// ─── Editing the categories themselves (2026-09-22) ─────────────────────────

export interface CategoryUsage {
  /** Projects holding manual or labor amounts under this category. */
  projectCount: number;
  manualCents: number;
  laborCents: number;
  /** Materials filed under it — they survive as Uncategorized. */
  specCount: number;
  /** Material categories mapped to it — the mapping is cleared. */
  mappedCategoryCount: number;
}

const plural = (n: number, one: string, many = `${one}s`): string => `${n} ${n === 1 ? one : many}`;

/**
 * What deleting a budget category will actually do, in the person's own
 * numbers. Budget categories are shared by every project in the firm, and the
 * amounts typed against one are deleted with it — so this never says just
 * "are you sure?".
 */
export function deleteCategoryWarning(name: string, usage: CategoryUsage): string {
  const amounts = usage.manualCents + usage.laborCents;
  const lines: string[] = [`Delete the budget category “${name}”?`];

  if (amounts > 0) {
    lines.push(
      `This removes ${fmtMoney(amounts)} of manual and labor amounts typed against it`
      + ` in ${plural(usage.projectCount, 'project')} — including this one. That can’t be undone.`,
    );
  }
  if (usage.specCount > 0) {
    lines.push(`${plural(usage.specCount, 'material')} filed under it will roll up as Uncategorized. Nothing is lost.`);
  }
  if (usage.mappedCategoryCount > 0) {
    lines.push(`${plural(usage.mappedCategoryCount, 'material category', 'material categories')} mapped to it will become unmapped.`);
  }
  if (lines.length === 1) lines.push('Nothing is filed under it, so nothing else changes.');
  // Categories are shared, so say so whenever another project could be hit.
  return lines.join('\n\n');
}
